import app from 'ampersand-app'
import keyBy from 'lodash/keyBy'
import type { RefObject } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import useSWR from 'swr'
import { packaging } from '../../models/packages'
import { logger } from '../../utils'
import { Combobox } from '../combobox/combobox'
import {
  bulkCreateTransactions,
  useCurrentEventSlug,
  useLocations,
  useTransactions
} from '../hooks'
import { TextButton } from '../text-button/text-button'
import type { Product } from '../types'
import * as styles from './dispatch-form.module.css'
import {
  DispatchFormTitle,
  type DispatchTransaction
} from './dispatch-form-shared'
import type { useTabletMode } from './toggle-tablet-mode'

const log = logger.extend('dispatch-form')

interface DispatchFormProps {
  onClose: () => void
  initialFocus: RefObject<HTMLSelectElement>
  isTabletMode: boolean
  setIsTabletMode: ReturnType<typeof useTabletMode>[1]
}

export function DispatchForm({
  onClose,
  initialFocus,
  isTabletMode,
  setIsTabletMode
}: DispatchFormProps) {
  const eventSlug = useCurrentEventSlug()
  const { mutate: mutateTransactions } = useTransactions()

  const { locations, storage, bars } = useLocations(eventSlug)
  const { data: products } = useSWR<Product[]>(
    `/api/events/${eventSlug}/products`
  )

  const productsById = keyBy(products || [], 'id')
  const productOptions = useMemo(
    () =>
      products
        ?.map((product) => ({
          value: product.id,
          label: product.name
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)) ?? [],
    [products]
  )

  const [sourceId, setSourceId] = useState('')
  const [targetId, setTargetId] = useState('')
  const [comment, setComment] = useState('')
  const [transactions, setTransactions] = useState<DispatchTransaction[]>([
    { productId: '', inputPackages: 0, inputUnits: 0 }
  ])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(
    function setInitialSource() {
      if (!sourceId && storage && locations) {
        const source = app.me.defaultSourceLocationId
          ? locations.find(
              (store) => store.id === app.me.defaultSourceLocationId
            )
          : storage[0]
        if (source) {
          setSourceId(source.id)
          log(`set initial source: ${source.name} (${source.id})`)
        }
      }
    },
    [locations, storage, sourceId]
  )

  // set target for user's with assigned location (e.g. bar bosses)
  useEffect(
    function setInitialTarget() {
      const locationId =
        app.me.defaultTargetLocationId || app.me.assignedLocationId
      if (!targetId && locations && locationId) {
        const target = locations.find((l) => l.id === locationId)
        if (target) {
          setTargetId(target.id)
          log(`set initial target: ${target.name} (${target.id})`)
        }
      }
    },
    [locations, targetId]
  )

  const isValid = Boolean(
    sourceId &&
      targetId &&
      transactions.some(
        (tx) => tx.productId && (tx.inputPackages > 0 || tx.inputUnits > 0)
      )
  )

  return (
    <form
      class='pure-form'
      onSubmit={async (event) => {
        event.preventDefault()

        setIsSubmitting(true)
        const payload = transactions
          .filter(
            (tx) => tx.productId && (tx.inputPackages > 0 || tx.inputUnits > 0)
          )
          .map((tx) => ({
            sourceId,
            targetId,
            productId: tx.productId,
            units:
              tx.inputPackages * productsById[tx.productId].packageSize +
              tx.inputUnits,
            comment
          }))

        try {
          await bulkCreateTransactions(eventSlug, payload)
          await mutateTransactions()
          onClose()
        } catch (error) {
          console.error(error)
        } finally {
          setIsSubmitting(false)
        }
      }}
    >
      <DispatchFormTitle
        targetName={locations?.find((l) => l.id === targetId)?.name}
        isTabletMode={isTabletMode}
        setIsTabletMode={setIsTabletMode}
        onClose={onClose}
      />

      <fieldset>
        <div class={styles.route}>
          <div>
            <label for='source'>Von</label>
            <select
              class='pure-input-1'
              name='source'
              value={sourceId}
              onChange={(event) => setSourceId(event.currentTarget.value)}
              ref={initialFocus}
              required
            >
              {storage && (
                <optgroup label='Lager'>
                  {storage.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {bars && (
                <optgroup label='Bars'>
                  {bars.map((bar) => (
                    <option key={bar.id} value={bar.id}>
                      {bar.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <label for='target'>Nach</label>
            <select
              class='pure-input-1'
              name='target'
              value={targetId}
              onChange={(event) => setTargetId(event.currentTarget.value)}
              required
            >
              <option disabled value=''>
                Ziel wählen...
              </option>
              {bars && (
                <optgroup label='Bars'>
                  {bars.map((bar) => (
                    <option key={bar.id} value={bar.id}>
                      {bar.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {storage && (
                <optgroup label='Lager'>
                  {storage.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>
      </fieldset>

      <fieldset>
        {transactions.map((tx, i) => (
          <div
            key={`transaction-${tx.productId || tx.inputPackages || Math.random()}`}
            class={styles.dispatchItemRow}
          >
            <Combobox
              name='productId'
              placeholder='Produkt auswählen...'
              value={tx.productId}
              options={productOptions}
              onChange={(option) => {
                const newTransactions = [...transactions]
                newTransactions[i] = {
                  ...newTransactions[i],
                  productId: option
                }
                setTransactions(newTransactions)
              }}
            />

            <div>
              <input
                class='pure-input-1'
                name='inputPackages'
                type='number'
                min='0'
                placeholder='0'
                value={tx.inputPackages || ''}
                onChange={(event) => {
                  const newTransactions = [...transactions]
                  newTransactions[i] = {
                    ...newTransactions[i],
                    inputPackages: event.currentTarget.valueAsNumber
                  }
                  setTransactions(newTransactions)
                }}
              />
              <span class='append'>
                {productsById && tx.productId
                  ? packaging.get(productsById[tx.productId].packageId).name
                  : 'Kiste'}
              </span>
            </div>
            <div>
              <input
                class='pure-input-1'
                name='inputUnits'
                type='number'
                min='0'
                step='any'
                placeholder='0'
                value={tx.inputUnits || ''}
                onChange={(event) => {
                  const newTransactions = [...transactions]
                  newTransactions[i] = {
                    ...newTransactions[i],
                    inputUnits: event.currentTarget.valueAsNumber
                  }
                  setTransactions(newTransactions)
                }}
              />
              <span class='append'>
                {productsById && tx.productId
                  ? packaging.get(productsById[tx.productId].unitId).name
                  : 'Flasche'}
              </span>
            </div>

            <button
              class='dispatch-item-remove'
              title='Entfernen...'
              type='button'
              tabIndex={-1}
              onClick={(event) => {
                event.preventDefault()
                setTransactions(transactions.filter((tx, index) => index !== i))
              }}
            >
              <i class='ion-ios-minus-outline' />
            </button>
          </div>
        ))}
      </fieldset>

      <div class={styles.buttons}>
        <TextButton
          onClick={(event) => {
            event.preventDefault()
            setTransactions([
              ...transactions,
              { productId: '', inputPackages: 0, inputUnits: 0 }
            ])
          }}
        >
          Weiteres Produkt hinzufügen...
        </TextButton>

        <div style={{ alignSelf: 'stretch' }}>
          <label htmlFor='comment'>Kommentar</label>
          <textarea
            name='comment'
            rows={3}
            style={{ width: '100%' }}
            value={comment}
            onChange={(event) => setComment(event.currentTarget.value)}
          />
        </div>

        <button
          class='pure-button pure-button-primary'
          type='submit'
          disabled={!isValid || isSubmitting}
        >
          Abschicken
        </button>
      </div>
    </form>
  )
}
